import { useMemo } from 'react';
import { QueryFunction, useQuery } from '@tanstack/react-query';
import { useWeb3Context, Web3Context } from '../web3Context';
import { useQueryDecoratedSilos } from '../decoratedSilo/hooks';
import { isUndefined } from '@silo/utils';
import { ChecksumAddress } from '@silo/types';
import { useQueryReadyFallbackMarkets } from '../markets/fallbackMarkets';

export const userSilosKey = 'userSilos';

type UserSilosKey = [key: typeof userSilosKey, web3Context: Web3Context, siloAddresses: ChecksumAddress[]];

const queryUserSilos: QueryFunction<ChecksumAddress[], UserSilosKey> = async ({
  queryKey: [, { user, contracts }, siloAddresses],
}) => {
  if (!user) return [];

  const silosWithPositionStatus = await Promise.all(
    siloAddresses.map((siloAddress) =>
      contracts.siloLens.hasPosition(siloAddress, user).then((hasPosition) => ({ siloAddress, hasPosition }))
    )
  );

  return silosWithPositionStatus.filter(({ hasPosition }) => hasPosition).map(({ siloAddress }) => siloAddress);
};

export const useQueryFallbackPositions = (enabled: boolean) => {
  const { data: fallbackMarkets } = useQueryReadyFallbackMarkets();
  const web3Context = useWeb3Context();

  const siloAddresses = useMemo(() => {
    if (!fallbackMarkets) return null;

    return fallbackMarkets.map(({ id }) => id);
  }, [fallbackMarkets]);

  const { data: userSilos, error } = useQuery(['userSilos', web3Context!, siloAddresses!], queryUserSilos, {
    enabled: enabled && !!web3Context && !!siloAddresses,
  });

  const { queries: decoratedSiloQueries } = useQueryDecoratedSilos(userSilos);

  const data = useMemo(() => {
    if (
      isUndefined(fallbackMarkets) ||
      isUndefined(userSilos) ||
      decoratedSiloQueries.some(({ data }) => isUndefined(data))
    ) {
      return undefined;
    }

    // asserting non-null data as the condition above verifies that data is not undefined
    return {
      markets: fallbackMarkets,
      decoratedSilos: decoratedSiloQueries.map(({ data }) => data!),
    };
  }, [fallbackMarkets, userSilos, decoratedSiloQueries]);

  return { data, error };
};
